import { useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BarChart3, Download, Printer } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { MoneyText } from '@/components/ui/MoneyText'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton'
import { StatTile } from '@/components/ui/StatTile'
import { Table, TBody, Td, THead, Th, Tr } from '@/components/ui/Table'
import { useProfile } from '@/auth/SessionProvider'
import { getChurchSettings } from '@/data/admin'
import { listDepartments } from '@/data/departments'
import { getChurchMonthSummary, getChurchSummary } from '@/data/reports'
import { quarterLabel } from '@/features/budgets/schemas'
import { DepartmentBarsChart } from '@/features/home/LazyCharts'
import { longDay } from '@/features/home/insights'
import { useMediaQuery } from '@/layouts/useMediaQuery'
import { downloadCsv } from '@/lib/csv'
import { formatMonth } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { currentQuarterId, lusakaToday, monthId } from '@/lib/periods'
import { previousQuarter, recentMonths, recentQuarters } from '@/lib/reportMath'
import { shiftMonth } from '@/lib/format'
import { churchMonthRows, churchSummaryRows } from '@/lib/reportCsv'
import { ChurchMonthView } from './ChurchMonthView'
import { PeriodSwitch } from './PeriodSwitch'
import type { ReportPeriod } from './PeriodSwitch'
import { Versus } from './reportParts'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/** Central treasury and auditors: every department side by side for a quarter. */
export function ChurchSummaryPage() {
  const profile = useProfile()
  const current = currentQuarterId()
  const thisMonth = monthId(lusakaToday())
  const [period, setPeriod] = useState<ReportPeriod>('quarter')
  const [quarter, setQuarter] = useState(current)
  const [month, setMonth] = useState(thisMonth)
  const lastMonth = shiftMonth(month, -1)
  const wide = useMediaQuery('(min-width: 1024px)')
  const last = previousQuarter(quarter)

  const depts = useQuery({ queryKey: ['departments', 'with-retired'], queryFn: () => listDepartments({ includeRetired: true }) })
  const settings = useQuery({ queryKey: ['church-settings'], queryFn: getChurchSettings, staleTime: 5 * 60_000 })
  const noncentral = (depts.data ?? []).filter((d) => !d.is_central)
  const idsKey = (depts.data ?? []).map((d) => d.id).join(',')
  const summary = useQuery({
    queryKey: ['church-summary', quarter, idsKey],
    queryFn: () => getChurchSummary(quarter, noncentral),
    enabled: depts.isSuccess && period === 'quarter',
  })
  // Last quarter, only for the comparison lines. If it cannot be loaded the lines are simply left out.
  const before = useQuery({
    queryKey: ['church-summary', last, idsKey],
    queryFn: () => getChurchSummary(last, noncentral),
    enabled: depts.isSuccess && period === 'quarter',
  })
  // The month view: this month, and the month before for the comparison lines.
  const monthRows = useQuery({
    queryKey: ['church-month', month, idsKey],
    queryFn: () => getChurchMonthSummary(month, noncentral),
    enabled: depts.isSuccess && period === 'month',
  })
  const monthBefore = useQuery({
    queryKey: ['church-month', lastMonth, idsKey],
    queryFn: () => getChurchMonthSummary(lastMonth, noncentral),
    enabled: depts.isSuccess && period === 'month',
  })
  const rows = summary.data ?? []
  const sum = (f: (r: (typeof rows)[number]) => number) => rows.reduce((s, r) => s + f(r), 0)
  const sumBefore = (f: (r: (typeof rows)[number]) => number) => (before.data ? before.data.reduce((s, r) => s + f(r), 0) : undefined)

  const loading = depts.isPending || summary.isPending
  const hasRows = period === 'quarter' ? rows.length > 0 : (monthRows.data?.length ?? 0) > 0
  const download = () => {
    if (period === 'quarter') downloadCsv(`church-summary-${quarter}.csv`, churchSummaryRows(rows, quarter))
    else if (monthRows.data) downloadCsv(`church-summary-${month}.csv`, churchMonthRows(monthRows.data, month))
  }
  const collected = sum((r) => r.collectedNgwee)
  const spent = sum((r) => r.spentNgwee)
  const overspent = spent > collected ? spent - collected : 0

  const tiles: { label: string; value: number; previous: number | undefined; goodWhen: 'up' | 'down' | 'none'; tone?: 'warning'; extra?: ReactNode }[] = [
    { label: 'Collected', value: collected, previous: sumBefore((r) => r.collectedNgwee), goodWhen: 'up' },
    {
      label: 'Spent',
      value: spent,
      previous: sumBefore((r) => r.spentNgwee),
      goodWhen: 'none',
      extra: overspent > 0 && <span className="text-wait">{formatKwacha(overspent)} more than was collected</span>,
    },
    { label: 'Sent to central', value: sum((r) => r.sentNgwee), previous: sumBefore((r) => r.sentNgwee), goodWhen: 'none' },
    {
      label: 'Sent but not confirmed',
      value: sum((r) => r.unconfirmedNgwee),
      previous: sumBefore((r) => r.unconfirmedNgwee),
      goodWhen: 'down',
      tone: sum((r) => r.unconfirmedNgwee) > 0 ? 'warning' : undefined,
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Church summary"
        description={`Every department for ${period === 'quarter' ? quarterLabel(quarter) : formatMonth(`${month}-01`)}.`}
        actions={
          <>
            <PeriodSwitch period={period} onChange={setPeriod} />
            {period === 'quarter' ? (
              <Select
                label="Quarter"
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
                options={recentQuarters(current, 6).map((id) => ({ value: id, label: quarterLabel(id) }))}
              />
            ) : (
              <Select
                label="Month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                options={recentMonths(thisMonth, 12).map((id) => ({ value: id, label: formatMonth(`${id}-01`) }))}
              />
            )}
            <Button size="lg" className="self-end" disabled={!hasRows} onClick={download}>
              <Download size={16} aria-hidden /> Download for Excel
            </Button>
            <Button variant="secondary" size="lg" className="self-end" disabled={!hasRows} onClick={() => window.print()}>
              <Printer size={16} aria-hidden /> Print
            </Button>
          </>
        }
      />

      {period === 'month' ? (
        <ChurchMonthView
          rows={monthRows.data}
          before={monthBefore.data}
          loading={depts.isPending || monthRows.isPending}
          error={depts.error ?? monthRows.error}
          retry={() => { void depts.refetch(); void monthRows.refetch() }}
          footer={`${settings.data?.churchName ?? 'Church Accounts'} · ${formatMonth(`${month}-01`)} · Generated ${longDay(lusakaToday())}`}
        />
      ) : null}

      {period === 'month' ? null : loading ? (
        <div role="status" aria-label="Loading" className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-card" />
            ))}
          </div>
          <Card>
            <CardBody>
              <SkeletonRows rows={5} />
            </CardBody>
          </Card>
        </div>
      ) : depts.isError || summary.isError ? (
        <div>
          <p role="alert" className="text-body text-bad">{errText(depts.error ?? summary.error)}</p>
          <Button variant="secondary" size="lg" className="mt-3" onClick={() => { void depts.refetch(); void summary.refetch() }}>Try again</Button>
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState icon={BarChart3} title="No departments yet" description="Departments appear here once they exist." />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Church totals">
            {tiles.map((t) => (
              <StatTile
                key={t.label}
                label={t.label}
                tone={t.tone}
                value={<MoneyText ngwee={t.value} />}
                detail={
                  <span className="flex flex-col gap-0.5">
                    <Versus current={t.value} previous={t.previous} goodWhen={t.goodWhen} />
                    {t.extra}
                  </span>
                }
              />
            ))}
          </div>

          <Card>
            <CardHeader title="Collected and spent, by department" />
            <CardBody>
              <DepartmentBarsChart wide={wide} data={rows.map((r) => ({ label: r.name, collectedNgwee: r.collectedNgwee, spentNgwee: r.spentNgwee }))} />
            </CardBody>
          </Card>

          <Card>
            <Table maxHeightClass="max-h-[70vh] print:max-h-none">
              <THead>
                <Tr className="hover:bg-transparent border-b-0">
                  <Th rowSpan={2} className="align-bottom">Department</Th>
                  <Th colSpan={4} scope="colgroup" className="text-center border-b border-line">Activity this quarter</Th>
                  <Th colSpan={3} scope="colgroup" className="text-center border-b border-line border-l">Position</Th>
                </Tr>
                <Tr className="hover:bg-transparent">
                  <Th numeric>Collected</Th>
                  <Th numeric>Spent</Th>
                  <Th numeric>Sent</Th>
                  <Th numeric>Confirmed</Th>
                  <Th numeric className="border-l border-line">Budget</Th>
                  <Th numeric>Cash on hand</Th>
                  <Th numeric>Dues owed</Th>
                </Tr>
              </THead>
              <TBody>
                {rows.map((d) => (
                  <Tr key={d.departmentId}>
                    <Td>
                      <Link to="/d/$deptId/report" params={{ deptId: d.departmentId }} className="font-medium text-brand hover:underline">{d.name}</Link>
                    </Td>
                    <Td numeric><MoneyText ngwee={d.collectedNgwee} /></Td>
                    <Td numeric><MoneyText ngwee={d.spentNgwee} /></Td>
                    <Td numeric><MoneyText ngwee={d.sentNgwee} /></Td>
                    <Td numeric>
                      <MoneyText ngwee={d.confirmedNgwee} />
                      {d.unconfirmedNgwee > 0 && <span className="block text-micro text-wait">{formatKwacha(d.unconfirmedNgwee)} not confirmed</span>}
                    </Td>
                    <Td numeric className="border-l border-line">
                      {d.budgetedNgwee === 0 ? (
                        <>
                          <span className="text-muted">None</span>
                          {profile.isAdmin && (
                            <Link to="/d/$deptId/budgets" params={{ deptId: d.departmentId }} className="block text-micro font-medium text-brand hover:underline no-print">
                              Set budget
                            </Link>
                          )}
                        </>
                      ) : (
                        <>
                          <MoneyText ngwee={d.budgetActualNgwee} strongZero /> <span className="text-muted">of</span> <MoneyText ngwee={d.budgetedNgwee} strongZero />
                        </>
                      )}
                    </Td>
                    <Td numeric><MoneyText ngwee={d.cashOnHandNgwee} /></Td>
                    <Td numeric><MoneyText ngwee={d.arrearsNgwee} /></Td>
                  </Tr>
                ))}
                <Tr total>
                  <Td>Total</Td>
                  <Td numeric><MoneyText ngwee={sum((r) => r.collectedNgwee)} strongZero /></Td>
                  <Td numeric><MoneyText ngwee={sum((r) => r.spentNgwee)} strongZero /></Td>
                  <Td numeric><MoneyText ngwee={sum((r) => r.sentNgwee)} strongZero /></Td>
                  <Td numeric><MoneyText ngwee={sum((r) => r.confirmedNgwee)} strongZero /></Td>
                  <Td numeric className="border-l border-line">
                    <MoneyText ngwee={sum((r) => r.budgetActualNgwee)} strongZero /> of <MoneyText ngwee={sum((r) => r.budgetedNgwee)} strongZero />
                  </Td>
                  <Td numeric><MoneyText ngwee={sum((r) => r.cashOnHandNgwee)} strongZero /></Td>
                  <Td numeric><MoneyText ngwee={sum((r) => r.arrearsNgwee)} strongZero /></Td>
                </Tr>
              </TBody>
            </Table>
          </Card>

          {/* Only on paper: who and when, repeated at the foot of every printed page. */}
          <p className="print-footer">
            {settings.data?.churchName ?? 'Church Accounts'} · {quarterLabel(quarter)} · Generated {longDay(lusakaToday())}
          </p>
        </>
      )}
    </div>
  )
}
